import { create } from 'zustand';
import { CancelledEvent, OrderStatus, OrderType, RejectedEvent } from '@/types';
import { getOrders } from '@/features/account/api/get-orders';
import { useAccountStore } from './use-account-store';
import { handleOrderForStore } from '@/utils/handle-store-data';

export type NewOrder = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: bigint;
  size: bigint;
  reduceOnly: boolean;
  status: OrderStatus.NEW;
  initMarginRatio: bigint;
  postTime: number;
  tpPrice?: bigint;
  slPrice?: bigint;
};

export type TPSLOrder = {
  symbol: string;
  id: string;
  isBuy: boolean;
  orderType: OrderType.TAKE_PROFIT | OrderType.STOP_LOSS;
  limitPrice: string;
  size: string;
  reduceOnly?: boolean;
  initMarginRatio?: string;
};

export type FilledOrder = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: bigint;
  tpPrice?: bigint;
  slPrice?: bigint;
  size: bigint;
  reduceOnly: boolean;
  status: OrderStatus.FILLED;
  initMarginRatio: bigint;
  lastFilledSize: bigint;
  lastFilledPrice: bigint;
  lastFilledTime: number;
  avgFilledPrice: bigint;
  realizedPnl: bigint;
  settledFunding: bigint;
  fees: bigint;
  postTime: number;
  markPrice: bigint;
};

export type CancelledOrder = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: bigint;
  tpPrice?: bigint;
  slPrice?: bigint;
  size: bigint;
  reduceOnly: boolean;
  status: OrderStatus.CANCELLED;
  initMarginRatio: bigint;
  postTime: number;
};

export type RejectedOrder = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: bigint;
  tpPrice?: bigint;
  slPrice?: bigint;
  size: bigint;
  reduceOnly: boolean;
  status: OrderStatus.REJECTED;
  initMarginRatio: bigint;
  postTime: number;
  code: number;
};

export type Order = NewOrder | FilledOrder | CancelledOrder | RejectedOrder;

type WalletScopedOrders = {
  orders: Record<string, Order>;
  filledOrderIds: Set<string>;
  newOrderIds: Set<string>;
  rejectedOrderIds: Set<string>;
  cancelledOrderIds: Set<string>;
  ordersToPoll: [string, OrderStatus, number][];
  pollOrdersIntervalId: NodeJS.Timeout | null;
  maxFilledStartTime: number; // used for getMarks to determine whether it needs API or store
};

interface OrdersStore {
  byWallet: Record<string, WalletScopedOrders>;
  getOrders: (wallet: string | undefined) => WalletScopedOrders;
  setOrder: (order: Order | CancelledEvent | RejectedEvent) => void;
  pollOrders: (
    newOrdersToPoll: [string, OrderStatus, number][],
    delay?: number,
  ) => void;
  clearOrdersStore: () => void;
  setMaxFilledStartTime: (time: number) => void;
}

export const cloneDefaultWalletScopedOrders = (): WalletScopedOrders => ({
  orders: {},
  filledOrderIds: new Set(),
  newOrderIds: new Set(),
  rejectedOrderIds: new Set(),
  cancelledOrderIds: new Set(),
  ordersToPoll: [],
  pollOrdersIntervalId: null,
  maxFilledStartTime: useAccountStore.getState().getAccurateTime(),
});

export const useOrdersStore = create<OrdersStore>((set, get) => ({
  byWallet: {},

  getOrders: (wallet) => {
    if (!wallet) return cloneDefaultWalletScopedOrders();

    const store = get().byWallet[wallet];
    return store ?? cloneDefaultWalletScopedOrders();
  },
  setMaxFilledStartTime: (time) => {
    const wallet = useAccountStore.getState().address;
    if (!wallet) {
      console.error(time, 'No wallet found in orders store');
      return;
    }
    const state = get();
    const store = state.byWallet[wallet] ?? cloneDefaultWalletScopedOrders();
    set({
      ...state,
      byWallet: {
        ...state.byWallet,
        [wallet]: { ...store, maxFilledStartTime: time },
      },
    });
  },
  setOrder: (order) => {
    const wallet = useAccountStore.getState().address;
    if (!wallet) {
      console.error(order, 'No wallet found in orders store');
      return;
    }

    const state = get();
    const store =
      wallet in state.byWallet
        ? { ...state.byWallet[wallet] }
        : cloneDefaultWalletScopedOrders();

    const existingOrder = store.orders[order.id];
    const updatedOrder = existingOrder ? { ...existingOrder, ...order } : order;

    store.orders[order.id] = updatedOrder as Order;

    switch (order.status) {
      case OrderStatus.NEW:
        if (
          !store.cancelledOrderIds.has(order.id) &&
          !store.filledOrderIds.has(order.id)
        ) {
          store.newOrderIds.add(order.id);
        }
        break;
      case OrderStatus.FILLED:
        store.filledOrderIds.add(order.id);
        store.newOrderIds.delete(order.id);
        if (order.lastFilledTime < store.maxFilledStartTime) {
          store.maxFilledStartTime = order.lastFilledTime;
        }
        break;
      case OrderStatus.REJECTED:
        store.rejectedOrderIds.add(order.id);
        store.newOrderIds.delete(order.id);
        break;
      case OrderStatus.CANCELLED:
        store.cancelledOrderIds.add(order.id);
        store.newOrderIds.delete(order.id);
        break;
    }

    set({
      byWallet: {
        ...state.byWallet,
        [wallet]: store,
      },
    });
  },

  pollOrders: (newOrdersToPoll, delay = 1500) => {
    const wallet = useAccountStore.getState().address;
    if (!wallet) return;

    const { byWallet } = get();
    const store = byWallet[wallet];
    if (!store) return;

    store.ordersToPoll.push(...newOrdersToPoll);

    const startPolling = () => {
      if (!store.pollOrdersIntervalId) {
        const intervalId = setInterval(() => {
          const now = useAccountStore.getState().getAccurateTime();

          const remaining = store.ordersToPoll.filter(([id, status, time]) => {
            if (now - time > 10000) return false;
            if (status === OrderStatus.NEW) return !store.newOrderIds.has(id);
            if (status === OrderStatus.FILLED)
              return !store.filledOrderIds.has(id);
            if (status === OrderStatus.REJECTED)
              return !store.rejectedOrderIds.has(id);
            if (status === OrderStatus.CANCELLED)
              return !store.cancelledOrderIds.has(id);
            return false;
          });

          store.ordersToPoll = remaining;

          if (remaining.length === 0) {
            clearInterval(intervalId);
            set({
              byWallet: {
                ...byWallet,
                [wallet]: { ...store, pollOrdersIntervalId: null },
              },
            });
            return;
          }

          getOrders({ id: remaining.map(([id]) => id) }).then((res) =>
            res.forEach((order) => handleOrderForStore(order)),
          );
        }, 1500);

        set({
          byWallet: {
            ...byWallet,
            [wallet]: { ...store, pollOrdersIntervalId: intervalId },
          },
        });
      }
    };

    if (delay > 0) {
      setTimeout(startPolling, delay);
    } else {
      startPolling();
    }
  },

  clearOrdersStore: () => {
    const { address: wallet, getAccurateTime } = useAccountStore.getState();
    if (!wallet) {
      console.error('No wallet found in orders store clear function.');
      return;
    }

    const store = get().byWallet[wallet];
    if (store?.pollOrdersIntervalId) clearInterval(store.pollOrdersIntervalId);

    set((state) => ({
      byWallet: {
        ...state.byWallet,
        [wallet]: {
          orders: {},
          filledOrderIds: new Set(),
          newOrderIds: new Set(),
          rejectedOrderIds: new Set(),
          cancelledOrderIds: new Set(),
          ordersToPoll: [],
          pollOrdersIntervalId: null,
          maxFilledStartTime: getAccurateTime(),
        },
      },
    }));
  },
}));
